"use client";
import { api } from "@/lib/api/api";
import { useQuery } from "@tanstack/react-query";

export interface UsePermissionsOptions {
  /** The additional-access endpoint is admin-only; pass false for anyone else. */
  includeAdditionalAccess?: boolean;
}

export function usePermissions(
  resourceIds: string[],
  { includeAdditionalAccess = true }: UsePermissionsOptions = {}
) {
  const {
    data: permissions,
    isLoading,
    isError,
    refetch: refetchPermissions
  } = useQuery({
    queryKey: ["organizations.resourceScheduledPermissionsAll", resourceIds],

    queryFn: async () => {
      const res =
        await api.client.organizations.resourceScheduledPermissionsAll(
          resourceIds
        );
      return res.result;
    }
  });

  const {
    data: additionalAccess,
    isLoading: isAdditionalLoading,
    isError: isAdditionalError,
    refetch: refetchAdditionalAccess
  } = useQuery({
    queryKey: ["organizations.getResourceAdditionalAccess", resourceIds],

    queryFn: async () => {
      const multiple = resourceIds.map(
        async (resId) =>
          (await api.client.organizations.getResourceAdditionalAccess(resId))
            .result ?? []
      );
      return Promise.all(multiple);
    },
    enabled: includeAdditionalAccess
  });

  const refresh = async () => {
    await refetchPermissions();
    // refetch() ignores `enabled: false`, so guard the admin-only call here.
    if (includeAdditionalAccess) await refetchAdditionalAccess();
  };

  const loading =
    !isLoading && !isError && !isAdditionalLoading && !isAdditionalError;
  return {
    refresh,
    permissions,
    additionalAccess,
    isError,
    loading,
    resourceIds
  };
}
